import React, { useState } from 'react';
import { inspectionsApi, RoomComponent } from '@/api/inspections';
import { Button, Input, cn } from '@/ui';

// "Add component" inline control — collapsed pill until tapped, then a small
// inline form. Lifted out of ComponentList.tsx during the 2026-05-01 split.

interface AddComponentControlProps {
  inspectionId: string;
  roomId: string;
  onComponentAdded: (component: RoomComponent) => void;
}

export const AddComponentControl: React.FC<AddComponentControlProps> = ({
  inspectionId,
  roomId,
  onComponentAdded,
}) => {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      const result = await inspectionsApi.addRoomComponent(inspectionId, roomId, {
        name: trimmed,
      });
      if (result.success && result.data) {
        onComponentAdded(result.data);
        setName('');
        setAdding(false);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const cancel = () => {
    setAdding(false);
    setName('');
  };

  if (!adding) {
    return (
      <button
        type="button"
        onClick={() => setAdding(true)}
        className={cn(
          'inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-caption font-medium',
          'border border-dashed border-brand/40 text-brand bg-transparent hover:bg-brand-soft/60 transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand',
        )}
      >
        <svg
          className="w-3.5 h-3.5"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          viewBox="0 0 24 24"
          aria-hidden
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
        </svg>
        Add component
      </button>
    );
  }

  return (
    <div className="p-3 bg-brand-soft/40 border border-brand/20 rounded-lg space-y-2">
      <Input
        inputSize="sm"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && name.trim()) handleSubmit();
          if (e.key === 'Escape') cancel();
        }}
        placeholder="Range hood, Exhaust fan, Microwave…"
        autoFocus
      />
      <div className="flex gap-2">
        <Button
          onClick={handleSubmit}
          disabled={!name.trim() || submitting}
          loading={submitting}
          className="flex-1"
          size="sm"
        >
          Add
        </Button>
        <Button variant="ghost" onClick={cancel} size="sm">
          Cancel
        </Button>
      </div>
    </div>
  );
};
